/**
 * Lexical-based read-only rich-text renderer.
 *
 * Despite the name, this is NOT a markdown parser. It expects a Lexical editor
 * state (JSON string) for `content` and renders it via Lexical nodes. Server-
 * side conversion (markdown / HTML / plain text -> Lexical JSON) lives in
 * `pollux-api/lib/contentConverter.js`. Name kept for back-compat.
 */
import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import {
    lexicalSerializedToPlainText,
    buildLexicalSingleParagraphState,
    buildLexicalFromPlainText,
    rootChildrenAreValid,
} from "../utils/lexicalContent";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MentionNode } from '../plugins/MentionsPlugin';
import { ImageNode } from '../nodes/ImageNode';
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";

import AutoLinkPlugin from "../plugins/AutoLinkPlugin";
// import ImagesPlugin from "../plugins/ImagesPlugin";
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

const DisplayContainer = styled(Box, { shouldForwardProp: (prop) => prop !== '$singleLine' })(({ theme, $singleLine }) => ({
    // border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: $singleLine ? 0 : theme.spacing(0, 0, 1),
    minWidth: $singleLine ? 0 : undefined,
    overflow: $singleLine ? 'hidden' : undefined,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    color: theme.palette.text.primary,
    '& a[href^="#comment-"]': {
        color: theme.palette.primary.main,
        fontFamily: 'monospace',
        fontWeight: theme.typography.fontWeightMedium,
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    ...($singleLine ? {} : {
        '& table': {
            borderCollapse: 'collapse',
            margin: theme.spacing(1, 0),
            width: 'auto',
        },
        '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(0.5, 1),
            textAlign: 'left',
            verticalAlign: 'top',
        },
        '& th': {
            backgroundColor: theme.palette.action.hover,
            fontWeight: theme.typography.fontWeightBold,
        },
        // Lexical wraps cell content in <p> — kill default paragraph margin inside cells.
        '& th p, & td p': {
            margin: 0,
        },
        '& code': {
            fontFamily: 'monospace',
            fontSize: theme.typography.size.md,
            backgroundColor: theme.palette.action.hover,
            padding: theme.spacing(0.25, 0.5),
            borderRadius: theme.shape.borderRadius / 2,
        },
        '& blockquote': {
            borderLeft: `4px solid ${theme.palette.divider}`,
            paddingLeft: theme.spacing(1.5),
            margin: theme.spacing(1, 0),
            color: theme.palette.text.secondary,
        },
        '& hr': {
            border: 'none',
            borderTop: `1px solid ${theme.palette.divider}`,
            margin: theme.spacing(2, 0),
        },
        // A nested list lives inside its own list item; that wrapper must not draw a marker of its own
        '& .rt-nested-listitem': {
            listStyle: 'none',
        },
        '& .rt-nested-listitem::before, & .rt-nested-listitem::after': {
            display: 'none',
        },
        '& ul ul, & ul ol, & ol ul, & ol ol': {
            margin: 0,
        },
        '& ul': { listStyleType: 'disc' },
        '& ul ul': { listStyleType: 'circle' },
        '& ul ul ul': { listStyleType: 'square' },
        '& .rt-strikethrough': { textDecoration: 'line-through' },
        '& .rt-underline': { textDecoration: 'underline' },
        '& .rt-underline-strikethrough': { textDecoration: 'underline line-through' },
    }),
}));

const ContentEditableStyled = styled(ContentEditable, {
    shouldForwardProp: (prop) => prop !== '$singleLine',
})(({ theme, $singleLine }) => ({
    outline: 'none',
    userSelect: 'text',
    ...($singleLine
        ? {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'normal',
          }
        : {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
          }),
}));


function editorStateForDisplay(content, singleLine) {
    if (!content) return undefined;

    let parsed = null;
    if (typeof content === 'string') {
        try {
            parsed = JSON.parse(content);
        } catch {
            parsed = null;
        }
    } else {
        parsed = content;
    }

    const isValidLexical = parsed?.root?.children != null && rootChildrenAreValid(parsed);

    if (!isValidLexical) {
        const fallbackPlain = parsed?.root?.children != null
            ? lexicalSerializedToPlainText(parsed)
            : (typeof content === 'string' ? content : '');
        if (singleLine) {
            const firstLine = (fallbackPlain.split(/\r?\n/)[0] || '').trimEnd();
            return buildLexicalSingleParagraphState(firstLine);
        }
        return buildLexicalFromPlainText(fallbackPlain);
    }

    if (!singleLine) return typeof content === 'string' ? content : JSON.stringify(content);

    try {
        const plain = lexicalSerializedToPlainText(parsed);
        const firstLine = (plain.split(/\r?\n/)[0] || '').trimEnd();
        return buildLexicalSingleParagraphState(firstLine);
    } catch {
        const firstLine = (String(content).split(/\r?\n/)[0] || '').trimEnd();
        return buildLexicalSingleParagraphState(firstLine);
    }
}

const COMMENT_REF_REGEX = /(^|[^\w#])#(\d+)\b/g;

function createCommentRefMatcher(validNumbers) {
    return (text) => {
        COMMENT_REF_REGEX.lastIndex = 0;
        let match;
        while ((match = COMMENT_REF_REGEX.exec(text)) !== null) {
            const number = Number(match[2]);
            if (!validNumbers.has(number)) continue;
            const index = match.index + match[1].length;
            return {
                index,
                length: match[2].length + 1,
                text: `#${number}`,
                url: `#comment-${number}`,
            };
        }
        return null;
    };
}

// A URL parser ignores tab/CR/LF and rejects an embedded NUL, so a scheme split by one of them
// ("java\0script:") must not read as harmless below.
// eslint-disable-next-line no-control-regex
const URL_IGNORED_CHARS_REGEX = /[\u0000\u0009\u000A\u000D]/g;
const SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
const RELATIVE_HREF_REGEX = /^(?:[#?]|\/(?!\/))/;
const NEW_TAB_SCHEMES = new Set(['http:', 'https:']);
const SAME_TAB_SCHEMES = new Set(['mailto:', 'tel:', 'sms:']);
const BLOCKED_HREF = 'about:blank';

// SECURITY: @lexical/link 0.17.1 hands back any href `new URL()` cannot parse unsanitised, skipping
// its own protocol check — this allowlist is the only protocol check left on render. Keep it.
function classifyHref(rawHref) {
    const href = String(rawHref ?? '').replace(URL_IGNORED_CHARS_REGEX, '').trim();
    if (!href) return 'blocked';
    const scheme = SCHEME_REGEX.exec(href)?.[0].toLowerCase();
    if (!scheme) return RELATIVE_HREF_REGEX.test(href) ? 'same-tab' : 'blocked';
    if (NEW_TAB_SCHEMES.has(scheme)) return 'new-tab';
    if (SAME_TAB_SCHEMES.has(scheme)) return 'same-tab';
    return 'blocked';
}

const NewTabLinksPlugin = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const apply = () => {
            const root = editor.getRootElement();
            if (!root) return;
            root.querySelectorAll('a[href]').forEach((anchor) => {
                const kind = classifyHref(anchor.getAttribute('href'));
                if (kind === 'blocked') {
                    anchor.setAttribute('href', BLOCKED_HREF);
                    anchor.removeAttribute('target');
                    anchor.removeAttribute('rel');
                    return;
                }
                if (kind !== 'new-tab') return;
                anchor.setAttribute('target', '_blank');
                anchor.setAttribute('rel', 'noopener noreferrer');
            });
        };
        const removeRootListener = editor.registerRootListener(apply);
        const removeUpdateListener = editor.registerUpdateListener(apply);
        return () => {
            removeRootListener();
            removeUpdateListener();
        };
    }, [editor]);

    return null;
};

const MarkdownDisplay = ({ content, singleLine = false, commentRefNumbers }) => {
    const editorState = useMemo(() => editorStateForDisplay(content, singleLine), [content, singleLine]);

    const extraMatchers = useMemo(() => {
        if (!commentRefNumbers?.size) return undefined;
        return [createCommentRefMatcher(commentRefNumbers)];
    }, [commentRefNumbers]);

    const initialConfig = {
        namespace: "MarkdownDisplay",
        nodes: [
            HeadingNode,
            QuoteNode,
            ListItemNode,
            ListNode,
            CodeHighlightNode,
            CodeNode,
            TableCellNode,
            TableNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode,
            MentionNode,
            ImageNode,
            HorizontalRuleNode
        ],
        onError: (error) => console.warn("Lexical display fallback:", error?.message || error),
        editable: false,
        editorState: editorState || undefined,
        theme: {
            text: {
                strikethrough: 'rt-strikethrough',
                underline: 'rt-underline',
                underlineStrikethrough: 'rt-underline-strikethrough',
            },
            list: {
                nested: {
                    listitem: 'rt-nested-listitem',
                },
            },
        },
    };

    return (
        <DisplayContainer $singleLine={singleLine}>
            <LexicalComposer key={editorState ?? content ?? 'empty'} initialConfig={initialConfig}>
                <RichTextPlugin
                    contentEditable={<ContentEditableStyled $singleLine={singleLine} />}
                    placeholder={<Typography>No content</Typography>}
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <LinkPlugin />
                <AutoLinkPlugin extraMatchers={extraMatchers} />
                <NewTabLinksPlugin />
                <ListPlugin />
                <HorizontalRulePlugin />
                {/* <ImagesPlugin /> */}
            </LexicalComposer>
        </DisplayContainer>
    );
};

export default MarkdownDisplay;
